import { useEffect, useRef, useMemo } from 'react'
import { useShallow } from 'zustand/react/shallow'
import queryClient from '@/queryClient'

import { QUERY_KEYS } from '../queries/cad_versions'
import { useCADQuery } from '@/services/queries/cads'
import { useCADPageStore } from '@/state'

import { QUERY_KEYS as VIEWS_QUERY_KEYS } from '@/services/queries/views/queryKeys'
import { QUERY_KEYS as DOCUMENT_VERSIONS_QUERY_KEYS } from '@/services/queries/document_versions/queryKeys'

export const useCADVersionProcessing = ({
  poll = 1000 * 5, // 5 second
  pollDocumentVersions,
}: {
  poll?: number
  pollDocumentVersions?: boolean
}) => {
  const iv = useRef<ReturnType<typeof setInterval> | null>(null)

  const { isLoading: isCadLoading, data: cadData } = useCADQuery()

  const processingStatus = cadData.version?.processing_status
  const cadVersionId = cadData.version?.id

  const addCompletedCadVersionUpload = useCADPageStore(
    useShallow((state) => state.addCompletedCadVersionUpload),
  )
  const completedCadVersionUploads = useCADPageStore(
    (state) => state.completedCadVersionUploads,
  )

  useEffect(() => {
    if (iv.current) {
      clearInterval(iv.current)
    }

    if (processingStatus && ['uploaded', 'new'].includes(processingStatus)) {
      iv.current = setInterval(() => {
        const cadVersionId = cadData.version?.id

        if (cadVersionId) {
          if (pollDocumentVersions) {
            void queryClient.invalidateQueries({
              queryKey: [DOCUMENT_VERSIONS_QUERY_KEYS.DOCUMENT_VERSIONS],
            })
          }
          void queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.CAD_VERSION, { cadVersionId }],
            refetchType: 'all',
          })
        }
      }, poll)
    }

    return () => {
      if (iv.current) {
        clearInterval(iv.current)
      }
    }
  }, [processingStatus, poll, cadData.cad?.id, cadData.version?.id])

  const isComplete = useMemo(() => {
    return (
      !isCadLoading &&
      processingStatus === 'completed' &&
      !cadData.hasNoDownloadUrl
    )
  }, [cadData.hasNoDownloadUrl, isCadLoading, processingStatus])

  const isProcessing = useMemo(() => {
    return !isCadLoading && cadData.hasNoDownloadUrl
  }, [isCadLoading, cadData.hasNoDownloadUrl])

  useEffect(() => {
    const hasBeenMarkedComplete = Boolean(
      cadVersionId && completedCadVersionUploads.includes(cadVersionId),
    )
    if (isComplete && cadVersionId && !hasBeenMarkedComplete) {
      addCompletedCadVersionUpload(cadVersionId)
      void queryClient.invalidateQueries({
        queryKey: [DOCUMENT_VERSIONS_QUERY_KEYS.DOCUMENT_VERSIONS],
      })
      void queryClient.invalidateQueries({
        queryKey: [VIEWS_QUERY_KEYS.VIEWS],
      })
    }
  }, [
    addCompletedCadVersionUpload,
    cadVersionId,
    completedCadVersionUploads,
    isComplete,
  ])

  return {
    data: {
      processingStatus,
      hasNoDownloadUrl: cadData.hasNoDownloadUrl,
      cadVersionId,
    },
    isLoading: isCadLoading,
    isProcessing,
    isComplete,
  }
}
